import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import axios from 'axios';

const AddressField = ({ 
  field, 
  value = {}, 
  onChange, 
  error,
  focusColor 
}) => {
  const [addressData, setAddressData] = useState({
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    country: 'Brasil',
    ...value
  });
  
  const [loading, setLoading] = useState(false);
  const [cepError, setCepError] = useState('');

  // Função para buscar dados do CEP
  const fetchAddressByCep = async (cep) => {
    if (!field.addressConfig?.enableAutocomplete) return;
    
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setLoading(true);
    setCepError('');

    try {
      const response = await axios.get(`/address/cep/${cleanCep}`);
      
      if (response.data.success) {
        const newAddressData = {
          ...addressData,
          zipCode: response.data.data.zipCode || cleanCep,
          street: response.data.data.street || '',
          neighborhood: response.data.data.neighborhood || '',
          city: response.data.data.city || '',
          state: response.data.data.state || '',
          country: response.data.data.country || 'Brasil'
        };
        
        setAddressData(newAddressData);
        onChange(newAddressData);
      } else {
        setCepError('CEP não encontrado');
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setCepError('Erro ao consultar CEP');
    } finally {
      setLoading(false);
    }
  };

  // Handler para mudanças nos campos
  const handleFieldChange = (fieldName, fieldValue) => {
    const newData = {
      ...addressData,
      [fieldName]: fieldValue
    };
    
    setAddressData(newData);
    onChange(newData);

    // Se for CEP e autocomplete estiver habilitado
    if (fieldName === 'zipCode' && field.addressConfig?.enableAutocomplete) {
      const cleanCep = fieldValue.replace(/\D/g, '');
      if (cleanCep.length === 8) {
        fetchAddressByCep(fieldValue);
      }
    }
  };

  // Função para formatar CEP
  const formatCep = (value) => {
    const cleanValue = value.replace(/\D/g, '');
    return cleanValue.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  // Capturar geolocalização se habilitado
  useEffect(() => {
    if (field.addressConfig?.enableGeolocation && navigator.geolocation && addressData.city && addressData.state) {
      const fullAddress = `${addressData.street} ${addressData.number}, ${addressData.neighborhood}, ${addressData.city}, ${addressData.state}, ${addressData.country}`;
      
      // Aqui você pode implementar a busca de coordenadas via API
      // Por exemplo, usando o serviço já criado no backend
      axios.post('/address/coordinates', { address: fullAddress })
        .then(response => {
          if (response.data.success && response.data.data) {
            const newData = {
              ...addressData,
              coordinates: response.data.data
            };
            setAddressData(newData);
            onChange(newData);
          }
        })
        .catch(error => {
          console.error('Erro ao obter coordenadas:', error);
        });
    }
  }, [addressData.street, addressData.number, addressData.city, addressData.state, field.addressConfig?.enableGeolocation]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {field.label}
        {field.required && <span style={{ color: 'red' }}> *</span>}
      </Typography>
      
      {cepError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {cepError}
        </Alert>
      )}

      <Grid container spacing={2}>
        {field.addressConfig.fields.zipCode && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="CEP"
              value={addressData.zipCode}
              onChange={(e) => handleFieldChange('zipCode', formatCep(e.target.value))}
              placeholder="00000-000"
              variant="outlined"
              size="small"
              required={field.required}
              error={!!error}
              InputProps={{
                endAdornment: loading && <CircularProgress size={20} />,
                sx: {
                  '&:focus-within': {
                    '& fieldset': {
                      borderColor: focusColor || 'primary.main'
                    }
                  }
                }
              }}
            />
          </Grid>
        )}

        {field.addressConfig.fields.street && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Rua"
              value={addressData.street}
              onChange={(e) => handleFieldChange('street', e.target.value)}
              variant="outlined"
              size="small"
              required={field.required}
              error={!!error}
              InputProps={{
                sx: {
                  '&:focus-within': {
                    '& fieldset': {
                      borderColor: focusColor || 'primary.main'
                    }
                  }
                }
              }}
            />
          </Grid>
        )}

        {field.addressConfig.fields.number && (
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Número"
              value={addressData.number}
              onChange={(e) => handleFieldChange('number', e.target.value)}
              variant="outlined"
              size="small"
              required={field.required}
              error={!!error}
              InputProps={{
                sx: {
                  '&:focus-within': {
                    '& fieldset': {
                      borderColor: focusColor || 'primary.main'
                    }
                  }
                }
              }}
            />
          </Grid>
        )}

        {field.addressConfig.fields.complement && (
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Complemento"
              value={addressData.complement}
              onChange={(e) => handleFieldChange('complement', e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                sx: {
                  '&:focus-within': {
                    '& fieldset': {
                      borderColor: focusColor || 'primary.main'
                    }
                  }
                }
              }}
            />
          </Grid>
        )}

        {field.addressConfig.fields.neighborhood && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Bairro"
              value={addressData.neighborhood}
              onChange={(e) => handleFieldChange('neighborhood', e.target.value)}
              variant="outlined"
              size="small"
              required={field.required}
              error={!!error}
              InputProps={{
                sx: {
                  '&:focus-within': {
                    '& fieldset': {
                      borderColor: focusColor || 'primary.main'
                    }
                  }
                }
              }}
            />
          </Grid>
        )}

        {field.addressConfig.fields.city && (
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Cidade"
              value={addressData.city}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              variant="outlined"
              size="small"
              required={field.required}
              error={!!error}
              InputProps={{
                sx: {
                  '&:focus-within': {
                    '& fieldset': {
                      borderColor: focusColor || 'primary.main'
                    }
                  }
                }
              }}
            />
          </Grid>
        )}

        {field.addressConfig.fields.state && (
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="UF"
              value={addressData.state}
              onChange={(e) => handleFieldChange('state', e.target.value.toUpperCase())}
              variant="outlined"
              size="small"
              required={field.required}
              error={!!error}
              inputProps={{ maxLength: 2 }}
              InputProps={{
                sx: {
                  '&:focus-within': {
                    '& fieldset': {
                      borderColor: focusColor || 'primary.main'
                    }
                  }
                }
              }}
            />
          </Grid>
        )}

        {field.addressConfig.fields.country && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="País"
              value={addressData.country}
              onChange={(e) => handleFieldChange('country', e.target.value)}
              variant="outlined"
              size="small"
              required={field.required}
              error={!!error}
              InputProps={{
                sx: {
                  '&:focus-within': {
                    '& fieldset': {
                      borderColor: focusColor || 'primary.main'
                    }
                  }
                }
              }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AddressField;